import { useTranslation } from 'react-i18next';
import { FaSolarPanel } from 'react-icons/fa6';
import { MdOutlineElectricBolt } from 'react-icons/md';

import { cn } from '@/lib/utils';

import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Callout } from '../ui/custom/callout';
import { Loader } from '../ui/loader';
import { useMemberForm } from './hooks/useMemberForm';
import { MemberFormParams } from './types/memberForm';

export const MemberRoleForm = ({
  member,
  title,
  submitText,
  cancel,
  onSuccess,
}: MemberFormParams) => {
  const { t } = useTranslation();
  const { register, onSubmit, watch, isPending } = useMemberForm(
    {
      ...member,
      member_role: member.member_role || 'CONSUMER',
    },
    onSuccess,
  );

  const memberRole = watch('member_role');

  return (
    <div className="overflow-auto flex flex-col gap-4 md:gap-8">
      {title}
      <form onSubmit={onSubmit}>
        <div className="flex flex-col md:flex-row gap-4">
          <label htmlFor="CONSUMER" className="w-full md:w-auto">
            <input
              {...register('member_role')}
              type="radio"
              className="hidden"
              id="CONSUMER"
              value="CONSUMER"
            />
            <Card
              className={cn(
                'w-full md:w-[172px] p-4 py-6 md:py-8 flex flex-col items-center gap-2 cursor-pointer',
                {
                  'bg-primary-background': memberRole === 'CONSUMER',
                },
              )}
            >
              <MdOutlineElectricBolt className="w-8 h-8" />
              <div className="text-primary text-sm">
                {t('registration.chooseYourRole.consumer')}
              </div>
            </Card>
          </label>
          <label htmlFor="PRODUCER" className="w-full md:w-auto">
            <input
              {...register('member_role')}
              type="radio"
              className="hidden"
              id="PRODUCER"
              value="PRODUCER"
            />
            <Card
              className={cn(
                'w-full md:w-[172px] p-4 py-6 md:py-8 flex flex-col items-center gap-2 cursor-pointer',
                {
                  'bg-primary-background': memberRole === 'PRODUCER',
                },
              )}
            >
              <FaSolarPanel className="w-8 h-8" />
              <div className="text-primary text-sm">
                {t('registration.chooseYourRole.producer')}
              </div>
            </Card>
          </label>
          <label htmlFor="PROSUMER" className="w-full md:w-auto">
            <input
              {...register('member_role')}
              type="radio"
              className="hidden"
              id="PROSUMER"
              value="PROSUMER"
            />
            <Card
              className={cn(
                'w-full md:w-[172px] p-4 py-6 md:py-8 flex flex-col items-center gap-2 cursor-pointer',
                {
                  'bg-primary-background': memberRole === 'PROSUMER',
                },
              )}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '24px',
                }}
              >
                <FaSolarPanel className="w-8 h-8" />
                <span>/</span>
                <MdOutlineElectricBolt className="w-8 h-8" />
              </div>
              <div className="text-primary text-sm">
                {t('registration.chooseYourRole.prosumer')}
              </div>
            </Card>
          </label>
        </div>
        <div>
          {memberRole === 'PRODUCER' && (
            <Callout
              className="mt-4"
              text={t('registration.chooseYourRole.producerCallout')}
            />
          )}
          {memberRole === 'CONSUMER' && (
            <Callout
              className="mt-4"
              text={t('registration.chooseYourRole.consumerCallout')}
            />
          )}
          {memberRole === 'PROSUMER' && (
            <Callout
              className="mt-4"
              text={t('registration.chooseYourRole.prosumerCallout')}
            />
          )}
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-2 sm:gap-0">
          {cancel && (
            <Button
              type="button"
              variant="secondary"
              className="sm:mr-4"
              onClick={cancel.onClick}
            >
              {cancel.text}
            </Button>
          )}
          <Button
            type="submit"
            className={cn({ 'cursor-progress': isPending })}
            disabled={isPending}
          >
            {isPending && <Loader className="mr-1" />}
            {submitText}
          </Button>
        </div>
      </form>
    </div>
  );
};
